import { z } from 'zod'

/**
 * Feature toggles (SPEC-20260814-1144).
 *
 * Ligar/desligar funcionalidades em runtime, sem deploy. A avaliacao e SEMPRE
 * do servidor: o cliente recebe um mapa `codigo -> boolean` ja resolvido (mesmo
 * padrao do bundle de i18n) e nunca reimplementa a regra — se cada plataforma
 * resolvesse por conta propria, web, mobile e desktop divergiriam.
 *
 * Cadeia de resolucao, do menos para o mais especifico:
 *
 *   seed (valor inicial) -> valor da instalacao (`enabled`, o "valor da
 *   empresa" — a instalacao e single-tenant) -> override por membro.
 *
 * Toggle desconhecido resolve como DESLIGADO (fail-closed): e mais seguro uma
 * funcionalidade nova nao aparecer do que aparecer por acidente.
 */

/**
 * Natureza do toggle — governa o ciclo de vida, nao a avaliacao:
 *  - RELEASE: esconde funcionalidade em desenvolvimento; tem dono e data-alvo
 *    de remocao (toggle de release que sobrevive ao release e divida tecnica);
 *  - OPS: kill-switch operacional permanente (desliga integracao problematica);
 *  - ENTITLEMENT: liga modulos conforme o plano contratado da instalacao.
 */
export const featureToggleKindSchema = z.enum(['RELEASE', 'OPS', 'ENTITLEMENT'])
export type FeatureToggleKind = z.infer<typeof featureToggleKindSchema>

export const featureToggleSchema = z.object({
  id: z.string().uuid(),
  /** Codigo tecnico, unico e imutavel depois de usado — e a chave do bundle. */
  code: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'use SCREAMING_SNAKE_CASE'),
  kind: featureToggleKindSchema,
  /** Texto livre para o admin — toggle nao e conteudo de usuario final. */
  description: z.string().nullable(),
  /** Valor da instalacao. O admin alterna aqui; overrides refinam por pessoa. */
  enabled: z.boolean(),
  ownerMemberId: z.string().uuid().nullable(),
  /** Data-alvo de remocao (ISO yyyy-mm-dd) — obrigatoria por convencao em RELEASE. */
  removalTargetDate: z.string().nullable(),
  /** Semeado pelo sistema: nao pode ser excluido nem ter o codigo alterado. */
  isSystem: z.boolean(),
})
export type FeatureToggle = z.infer<typeof featureToggleSchema>

/** Override por membro: vence o valor da instalacao para aquela pessoa. */
export const featureToggleOverrideSchema = z.object({
  id: z.string().uuid(),
  featureToggleId: z.string().uuid(),
  memberId: z.string().uuid(),
  enabled: z.boolean(),
  /** Por que a excecao existe — aparece na tela do admin. */
  reason: z.string().nullable(),
})
export type FeatureToggleOverride = z.infer<typeof featureToggleOverrideSchema>

/** Excecao por workspace (SPEC-20260814-1333): vale para qualquer natureza. */
export const featureToggleWorkspaceOverrideSchema = z.object({
  id: z.string().uuid(),
  featureToggleId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  enabled: z.boolean(),
  reason: z.string().nullable(),
})
export type FeatureToggleWorkspaceOverride = z.infer<typeof featureToggleWorkspaceOverrideSchema>

/** Plano comercial: agrupa os toggles ENTITLEMENT que o pacote inclui. */
export const planSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
})
export type Plan = z.infer<typeof planSchema>

/**
 * O minimo que a resolucao precisa saber de um toggle. `kind` e string larga
 * (o banco guarda varchar) — a regra so compara com 'ENTITLEMENT'.
 */
export type ResolvableToggle = Pick<FeatureToggle, 'id' | 'code' | 'enabled'> & { kind: string }
export type ResolvableOverride = Pick<FeatureToggleOverride, 'featureToggleId' | 'memberId' | 'enabled'>
export type ResolvableWorkspaceOverride = Pick<
  FeatureToggleWorkspaceOverride,
  'featureToggleId' | 'workspaceId' | 'enabled'
>

/**
 * Para quem o bundle esta sendo resolvido.
 *
 * `planFeatureToggleIds` sao os toggles ENTITLEMENT inclusos no plano do
 * workspace (null quando o workspace nao tem plano — nesse caso vale o valor
 * da instalacao, o comportamento anterior a SPEC-20260814-1333).
 */
export type FeatureToggleContext = {
  memberId: string | null
  workspaceId: string | null
  planFeatureToggleIds: ReadonlySet<string> | null
}

/**
 * Valor efetivo de UM toggle para o contexto dado.
 *
 * Precedencia (SPEC-20260814-1333):
 *   instalacao OFF (master) > override do membro > override do workspace
 *   > plano do workspace (so ENTITLEMENT) > default (instalacao ON).
 *
 * `enabled=false` na instalacao desliga para TODO MUNDO — e o kill-switch
 * global: plano e overrides so refinam quando o master esta ligado.
 */
export function resolveFeatureToggleValue(
  toggle: ResolvableToggle,
  overrides: readonly ResolvableOverride[],
  workspaceOverrides: readonly ResolvableWorkspaceOverride[],
  context: FeatureToggleContext,
): boolean {
  if (!toggle.enabled) return false

  if (context.memberId !== null) {
    const doMembro = overrides.find(
      (o) => o.featureToggleId === toggle.id && o.memberId === context.memberId,
    )
    if (doMembro) return doMembro.enabled
  }

  if (context.workspaceId !== null) {
    const doWorkspace = workspaceOverrides.find(
      (o) => o.featureToggleId === toggle.id && o.workspaceId === context.workspaceId,
    )
    if (doWorkspace) return doWorkspace.enabled

    // Plano so governa entitlement: modulo fora do pacote contratado = desligado.
    if (toggle.kind === 'ENTITLEMENT' && context.planFeatureToggleIds !== null) {
      return context.planFeatureToggleIds.has(toggle.id)
    }
  }

  return true
}

/**
 * Mapa `codigo -> boolean` resolvido para o contexto — e o payload do endpoint
 * `/feature-toggles/bundle`. Toggles arquivados nao devem chegar aqui (o
 * chamador filtra); codigo ausente do mapa e tratado como desligado no cliente.
 */
export function resolveFeatureToggleBundle(
  toggles: readonly ResolvableToggle[],
  overrides: readonly ResolvableOverride[],
  workspaceOverrides: readonly ResolvableWorkspaceOverride[],
  context: FeatureToggleContext,
): Record<string, boolean> {
  const bundle: Record<string, boolean> = {}
  for (const toggle of toggles) {
    bundle[toggle.code] = resolveFeatureToggleValue(toggle, overrides, workspaceOverrides, context)
  }
  return bundle
}

// --- Modulos vendaveis --------------------------------------------------

/**
 * Modulos do shell que podem ser contratados por plano. `admin` (a AREA) nao
 * entra: as telas do admin sao vendidas UMA A UMA (ver
 * ENTITLEMENT_ADMIN_SECTION_IDS). A lista canonica vive no dominio para o seed
 * e a web derivarem o mesmo codigo — modulo novo no rail exige entrada nova
 * aqui (e seed), senao o gate o trata como nao contratavel (sempre visivel).
 */
export const ENTITLEMENT_MODULE_IDS = [
  'work-items',
  'kanbans',
  'calendar',
  'meetings',
  'mail',
  'docs',
  'apps',
  'portfolio',
  'channels',
  'groups',
  'status',
  'broadcast',
  'automations',
  'analytics',
  // SPEC-20260814-1427: o sino/central de notificacoes tambem e vendavel
  'notifications',
] as const
export type EntitlementModuleId = (typeof ENTITLEMENT_MODULE_IDS)[number]

/** `work-items` -> `MODULE_WORK_ITEMS` — o codigo do toggle do modulo. */
export function moduleToggleCode(moduleId: string): string {
  return `MODULE_${moduleId.replace(/-/g, '_').toUpperCase()}`
}

/** O modulo e vendavel (tem toggle de entitlement)? */
export function isEntitlementModule(moduleId: string): moduleId is EntitlementModuleId {
  return (ENTITLEMENT_MODULE_IDS as readonly string[]).includes(moduleId)
}

/**
 * Telas da Administracao vendidas UMA A UMA por plano (SPEC-20260814-1427).
 *
 * `feature-toggles` e `plans` NUNCA entram aqui (decisao do usuario,
 * anti-lockout): sao o painel que gerencia os proprios planos — se um plano
 * pudesse escondê-las de todo mundo, o destravamento só sairia via banco.
 */
export const ENTITLEMENT_ADMIN_SECTION_IDS = [
  'members',
  'companies',
  'areas',
  'work-item-types',
  'work-item-statuses',
  'priorities',
  'relation-types',
  // Fluxos de trabalho (SPEC-20260815-1204). Toggle novo em ambiente EXISTENTE
  // exige migration de seed que tambem o acrescenta aos planos ja cadastrados
  // (ver 0030): preserva o "todas as telas" de quem contratou antes da tela.
  'flows',
  'locales',
  'integrations',
  // Plugins (SPEC-20260926-2036): a tela onde o administrador instala o que o
  // super admin cadastrou. Mesma regra da 0030 — toggle novo em ambiente que
  // ja existe exige seed que tambem o acrescenta aos planos cadastrados.
  'plugins',
  // Conectores de IA (SPEC-20261103-1818): o administrador libera quem pode
  // usar o Orbind pelo Claude. Mesma regra da 0030/0118: a migration 0144
  // acrescenta o toggle novo aos planos ja cadastrados.
  'ai-connectors',
  'audit',
] as const
export type EntitlementAdminSectionId = (typeof ENTITLEMENT_ADMIN_SECTION_IDS)[number]

/** `work-item-types` -> `ADMIN_WORK_ITEM_TYPES` — o codigo do toggle da tela. */
export function adminSectionToggleCode(sectionId: string): string {
  return `ADMIN_${sectionId.replace(/-/g, '_').toUpperCase()}`
}

/** A tela do admin e vendavel (tem toggle de entitlement)? */
export function isEntitlementAdminSection(sectionId: string): sectionId is EntitlementAdminSectionId {
  return (ENTITLEMENT_ADMIN_SECTION_IDS as readonly string[]).includes(sectionId)
}

// Seeds em `catalog.ts`, junto dos demais catalogos semeados da instalacao.
